import React, { useState, useEffect } from 'react';
import { format, parseISO, isValid } from 'date-fns';
import { es } from 'date-fns/locale';
import { useAuth } from '../../context/AuthContext';
import { IconWallet, IconCalendar, IconNote } from './utils/icons';
import { getDerivedStatus, StatusPill } from './utils/helpers';
import { PriorityBadge } from './TicketsView';
import { derivePriorityFromTags } from './modals/TicketModal';

export function DashboardView() {
  const { token } = useAuth();
  const [data, setData] = useState({
    upcomingPayments: [],
    upcomingMeetings: [],
    recentNotes: [],
    totalsByCurrency: {},
    counts: { atrasado:0, por_vencer:0, pendiente:0 },
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const formatMoney = (amount, currency='MXN') =>
    new Intl.NumberFormat('es-MX', { style: 'currency', currency }).format(Number(amount) || 0);

  const formatDay = (iso) => {
    if (!iso) return '—';
    const d = parseISO(iso);
    return isValid(d) ? format(d, "dd/MM", { locale: es }) : '—';
  };

  const formatDateTime = (iso) => {
    if (!iso) return '—';
    const d = parseISO(iso);
    return isValid(d) ? format(d, "EEE d MMM • HH:mm", { locale: es }) : '—';
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setError('');

      try {
        const next7 = new Date();
        next7.setDate(next7.getDate() + 7);
        const toDateStr = next7.toISOString().split('T')[0];
        const nowIso = new Date().toISOString();

        const paymentsRes = await fetch(`/api/owner/payments?toDate=${toDateStr}&sortBy=due_date&sortOrder=ASC&_=${Date.now()}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const paymentsAll = await paymentsRes.json();
        if (!paymentsRes.ok) throw new Error(paymentsAll?.mensaje || 'No se pudieron cargar pagos');

        const upcomingPayments = (paymentsAll || [])
          .filter(p => ['pendiente','atrasado'].includes(p.status))
          .sort((a,b) => new Date(a.due_date||0) - new Date(b.due_date||0));

        const counts = { atrasado:0, por_vencer:0, pendiente:0 };
        const totalsByCurrency = {};
        for (const p of upcomingPayments) {
          const currency = p.currency || 'MXN';
          totalsByCurrency[currency] = (totalsByCurrency[currency] || 0) + Number(p.amount || 0);
          const st = getDerivedStatus(p);
          if (st === 'atrasado') counts.atrasado++;
          else if (st === 'por_vencer') counts.por_vencer++;
          else if (st === 'pendiente') counts.pendiente++;
        }

        const meetingsRes = await fetch(`/api/owner/meetings?start=${encodeURIComponent(nowIso)}&end=${encodeURIComponent(next7.toISOString())}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const meetingsAll = await meetingsRes.json();
        if (!meetingsRes.ok) throw new Error(meetingsAll?.mensaje || 'No se pudieron cargar reuniones');

        const notesRes = await fetch(`/api/owner/notes`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const allNotes = await notesRes.json();
        if (!notesRes.ok) throw new Error(allNotes?.mensaje || 'No se pudieron cargar tickets');

        const recentNotes = (Array.isArray(allNotes)? allNotes: [])
          .sort((a,b)=> (b.pinned - a.pinned) ||
            (new Date(b.updated_at||b.created_at||0) - new Date(a.updated_at||a.created_at||0)))
          .slice(0,5)
          .map(n => ({ ...n, priority: derivePriorityFromTags(n.tags) }));

        if (!alive) return;
        setData({
          upcomingPayments,
          upcomingMeetings: meetingsAll || [],
          recentNotes,
          totalsByCurrency,
          counts
        });
      } catch (e) {
        if (alive) setError(e.message || 'Error de conexión.');
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => { alive = false; };
  }, [token]);

  const payments = data.upcomingPayments || [];
  const meetings = data.upcomingMeetings || [];
  const notes = data.recentNotes || [];

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {error && (
        <div className="md:col-span-3 rounded-2xl border border-rose-200 bg-rose-50 p-5 text-rose-900">
          <div className="font-semibold">No se pudo cargar el dashboard</div>
          <p className="text-sm opacity-80 mt-1">{error}</p>
        </div>
      )}

      {loading && (
        <>
          {[0,1,2].map(i => (
            <div key={i} className="rounded-2xl border border-slate-200/70 bg-white/80 p-6 shadow-sm animate-pulse">
              <div className="h-5 w-48 bg-slate-200 rounded mb-4" />
              {Array.from({length:4}).map((_,j)=>(
                <div key={j} className="flex items-center justify-between py-2">
                  <div className="h-3 w-40 bg-slate-200 rounded" />
                  <div className="h-3 w-24 bg-slate-200 rounded" />
                </div>
              ))}
            </div>
          ))}
        </>
      )}

      {!loading && (
        <>
          <div className="md:col-span-3 grid gap-4 md:grid-cols-3">
            <div className="rounded-2xl border border-slate-200/70 bg-white/80 p-5 shadow-sm">
              <h4 className="font-semibold text-slate-800 flex items-center gap-2">
                <IconWallet/> Total por pagar ≤ 7 días
              </h4>
              {Object.keys(data.totalsByCurrency).length === 0 ? (
                <p className="text-slate-500 mt-2 text-sm">Sin pagos próximos.</p>
              ) : (
                <ul className="mt-3 space-y-1">
                  {Object.entries(data.totalsByCurrency).map(([cur, amt])=>(
                    <li key={cur} className="flex items-center justify-between">
                      <span className="text-sm text-slate-600">{cur}</span>
                      <span className="text-sm font-semibold text-slate-900">
                        {formatMoney(amt, cur)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              <div className="mt-3 flex flex-wrap gap-2">
                <span className="rounded-full bg-rose-50 text-rose-700 px-3 py-1 text-xs ring-1 ring-rose-200">
                  Atrasados: <b>{data.counts.atrasado}</b>
                </span>
                <span className="rounded-full bg-amber-50 text-amber-800 px-3 py-1 text-xs ring-1 ring-amber-200">
                  Por vencer: <b>{data.counts.por_vencer}</b>
                </span>
                <span className="rounded-full bg-indigo-50 text-indigo-700 px-3 py-1 text-xs ring-1 ring-indigo-200">
                  Pendientes: <b>{data.counts.pendiente}</b>
                </span>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200/70 bg-white/80 p-5 shadow-sm">
              <h4 className="font-semibold text-slate-800 flex items-center gap-2">
                <IconCalendar/> Próximas reuniones (7 días)
              </h4>
              {meetings.length === 0 ? (
                <p className="text-slate-500 mt-2 text-sm">No hay reuniones próximas.</p>
              ) : (
                <ul className="mt-3 space-y-2">
                  {meetings.slice(0, 6).map(m => (
                    <li key={m.id} className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium text-slate-800">{m.title}</div>
                        <div className="text-xs text-slate-500">{formatDateTime(m.start_time)}{m.location ? ` • ${m.location}` : ''}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="rounded-2xl border border-slate-200/70 bg-white/80 p-5 shadow-sm">
              <h4 className="font-semibold text-slate-800 flex items-center gap-2">
                <IconNote/> Pendientes recientes
              </h4>
              {notes.length === 0 ? (
                <p className="text-slate-500 mt-2 text-sm">No hay tickets recientes.</p>
              ) : (
                <ul className="mt-3 space-y-3">
                  {notes.slice(0, 5).map(n => (
                    <li key={n.id} className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium text-slate-800">
                          {n.title || 'Sin título'}
                        </div>
                        <div className="text-xs text-slate-500 truncate max-w-[36ch]">
                          {n.content || '—'}
                        </div>
                      </div>
                      <PriorityBadge priority={n.priority || 'media'} />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {payments.length > 0 && (
            <div className="md:col-span-3 rounded-2xl border border-slate-200/70 bg-white/80 p-5 shadow-sm">
              <h4 className="font-semibold text-slate-800 flex items-center gap-2">
                <IconWallet/> Pagos próximos (todas las empresas)
              </h4>
              <ul className="mt-3 divide-y divide-slate-100">
                {payments.slice(0, 10).map(p => (
                  <li key={p.id} className="py-2.5 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-slate-800 truncate">{p.title}</div>
                      <div className="text-xs text-slate-500">
                        {p.company} • Vence {formatDay(p.due_date)} • {p.category}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <StatusPill status={p.status} due_date={p.due_date} />
                      <div className="text-sm font-semibold text-slate-900">
                        {formatMoney(p.amount, p.currency)}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}
